import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/base/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta(
    drama
      ? `${drama.title} - 仙剑奇侠传影视站剧情介绍`
      : "剧集未找到 - 仙剑奇侠传影视站",
    drama
      ? `${drama.title}（${drama.year}）剧情介绍与角色分析，包含分集剧情简介、主要角色形象与演员阵容，带你深入了解这部经典仙侠剧。`
      : "未找到对应的剧集内容，请返回分类页浏览其他仙侠剧集。",
    drama
      ? `${drama.title},${drama.title}剧情,${drama.title}角色,仙剑奇侠传,仙侠剧`
      : "仙剑奇侠传,仙侠剧"
  );

  const related = useMemo(() => {
    if (!drama) return [];
    const sameCategory = dramas.filter(
      (item) => item.id !== drama.id && item.category === drama.category
    );
    const others = dramas.filter(
      (item) => item.id !== drama.id && item.category !== drama.category
    );
    return [...sameCategory, ...others].slice(0, 4);
  }, [drama]);

  if (!drama) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-3xl">
          <i className="ri-movie-2-line"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl text-foreground-950">内容不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">
          参数有误或该剧集已下线，请返回分类页浏览其他仙侠剧集。
        </p>
        <Link
          to="/category"
          className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-700 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回电视剧分类
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(drama.id);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "电视剧分类", to: "/category" }, { label: drama.title }]} />
      </div>

      <section className="relative w-full mt-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={drama.banner}
            alt={`${drama.title}主视觉`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/85 to-background-50/40"></div>
        </div>

        <div className="relative w-full px-4 md:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-3 w-40 md:w-52 lg:w-full shrink-0">
              <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200 bg-background-200">
                <img
                  src={drama.poster}
                  alt={`${drama.title}海报`}
                  title={`${drama.title} - 仙剑奇侠传影视站`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-9">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-800 text-xs">
                  {drama.category}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                  {drama.region}地区
                </span>
                <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs">
                  {drama.era}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-foreground-950">
                {drama.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-700">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="font-medium text-foreground-950">
                    {drama.rating.toFixed(1)}
                  </strong>
                  分
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-primary-700"></i>
                  {drama.year} 年首播
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-film-line text-primary-700"></i>
                  全 {drama.episodes} 集
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-accent-600"></i>
                  热度 {drama.heat}
                </span>
              </div>

              <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">
                {drama.summary}
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm max-w-2xl">
                <p className="flex gap-2">
                  <span className="text-foreground-500 shrink-0">导演</span>
                  <span className="text-foreground-900">{drama.director}</span>
                </p>
                <p className="flex gap-2">
                  <span className="text-foreground-500 shrink-0">编剧</span>
                  <span className="text-foreground-900">{drama.writer}</span>
                </p>
                <p className="flex gap-2 sm:col-span-2">
                  <span className="text-foreground-500 shrink-0">主演</span>
                  <span className="text-foreground-900">{drama.cast.join("、")}</span>
                </p>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/play/${drama.id}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-700 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-play-circle-line"></i>
                  进入播放演示
                </Link>
                <button
                  type="button"
                  onClick={() => toggleFavorite(drama.id)}
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    favorited
                      ? "bg-accent-500 hover:bg-accent-400 text-foreground-950"
                      : "border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400 hover:text-accent-800"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {favorited ? "已收藏" : "收藏本剧"}
                </button>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-400 hover:text-primary-700 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回分类页
                </Link>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {drama.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full bg-background-100 border border-background-200 text-foreground-600 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <article className="lg:col-span-8">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 flex items-center gap-3">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              剧情简介
            </h2>
            <div className="mt-5 space-y-4">
              {drama.synopsis.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-sm md:text-base text-foreground-700 leading-[1.9]"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <h2 className="mt-12 font-heading text-xl md:text-2xl font-semibold text-foreground-950 flex items-center gap-3">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              角色详解
            </h2>
            <div className="mt-5 -mx-4 px-4 md:mx-0 md:px-0 flex gap-4 overflow-x-auto lg:grid lg:grid-cols-2 lg:gap-5 lg:overflow-visible pb-2">
              {drama.characters.map((character) => (
                <div
                  key={character.id}
                  className="w-64 lg:w-auto shrink-0 rounded-lg border border-background-200 bg-background-50 overflow-hidden flex"
                >
                  <div className="w-24 lg:w-28 shrink-0 bg-background-200">
                    <img
                      src={character.image}
                      alt={`${character.name}角色形象`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading text-base font-semibold text-foreground-950">
                        {character.name}
                      </h3>
                      <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 text-[11px]">
                        {character.role}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-500">
                      {character.actor} 饰
                    </p>
                    <p className="mt-2 text-xs text-foreground-600 leading-relaxed line-clamp-4">
                      {character.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 lg:sticky lg:top-24">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-bookmark-3-line text-primary-700"></i>
                我的收藏
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                收藏状态保存在你的浏览器本地，随时回来都能快速找到这部剧集。
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
                    favorited
                      ? "bg-accent-100 text-accent-800"
                      : "bg-background-100 text-foreground-500"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {favorited ? "已加入收藏" : "尚未收藏"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => toggleFavorite(drama.id)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 h-10 rounded-md border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-700 text-sm whitespace-nowrap transition-colors cursor-pointer"
              >
                {favorited ? "取消收藏" : "加入收藏"}
              </button>

              <div className="mt-6 pt-5 border-t border-background-200">
                <h3 className="text-sm font-medium text-foreground-900 mb-3">剧集速览</h3>
                <ul className="flex flex-col gap-2.5 text-sm">
                  <li className="flex justify-between">
                    <span className="text-foreground-500">副分类</span>
                    <span className="text-foreground-900">{drama.category}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-foreground-500">首播年份</span>
                    <span className="text-foreground-900">{drama.year}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-foreground-500">集数</span>
                    <span className="text-foreground-900">{drama.episodes} 集</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-foreground-500">地区</span>
                    <span className="text-foreground-900">{drama.region}</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-16">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
          相关推荐
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {related.map((item) => (
            <DramaCard key={item.id} drama={item} />
          ))}
        </div>
      </section>
    </div>
  );
}